import secureApi from "../../utils/secureApi";

const TRIAL_ACADEMIES_ENDPOINT = "/api/v1/academy/website/academy-by-sports";
const TRIAL_CENTERS_ENDPOINT = "/api/v1/academy/website/list-centers";
const TRIAL_COURSES_ENDPOINT = "/api/v1/trials/website/list-courses";
const TRIAL_AGE_CATEGORIES_ENDPOINT = "/api/v1/trials/website/list-age-category";
const TRIAL_AVAILABLE_SLOTS_ENDPOINT = "/api/v1/batches/trial/list-available-slots";
const TRIAL_SLOTS_FOR_DATE_ENDPOINT = "/api/v1/batches/trial/website/fetch-slots";
const ADD_TRIAL_ENDPOINT = "/api/v2/trials/admin/add-trial";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface TrialAcademy {
  _id: string;
  basicDetails: {
    academyName: string;
    logo: string;
    sports: string[];
  };
  slug: string;
}

export interface TrialCenterAcademyInfo {
  _id: string;
  basicDetails: {
    academyName: string;
    logo: string;
    sports: string[];
  };
  slug: string;
}

export interface TrialCenter {
  _id: string;
  academyInfo: TrialCenterAcademyInfo;
  basicDetails: {
    _id?: string;
    address: string;
    location: string;
    logo: string;
    city?: string;
    coordinator?: string;
  };
  slug: string;
}

export interface TrialCourse {
  _id: string;
  courseName: string;
  proficiency: string;
}

export interface TrialAvailableSlotItem {
  date: string;
  day: string;
  endTime: string;
  startTime: string;
}

export interface TrialAvailableSlotBatch {
  _id: string;
  availableSlots: TrialAvailableSlotItem[];
  batchName: string;
  batchType: string;
  coachingCenter: string;
  course: string;
  sports: string;
}

export interface TrialSlot {
  startTime: string;
  endTime: string;
}

export interface TrialSlotsForDateResponse {
  _id: string;
  slots: TrialSlot[];
}

export interface AddTrialPayload {
  fullName: string;
  countryCode: number;
  phoneNumber: number;
  academy: string;
  coachingCenter: string;
  bookingType: "free-trial";
  sports: string;
  course: string;
  ageCategory: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  registeredThrough: "web";
  source: "admin";
  enrolledBy: "self" | "parent";
  parentName?: string;
  relationship?: string;
}

interface ApiListResponse<T> {
  data: T;
  message: string;
  status: string;
  statusCode: number;
}

interface PaginatedResponse<T> {
  paginatedResults: T[];
  totalCount: number;
}

// ─── API Functions ──────────────────────────────────────────────────────────

export const fetchAcademiesBySport = (sport: string) =>
  secureApi.get<ApiListResponse<TrialAcademy[]>>(
    `${TRIAL_ACADEMIES_ENDPOINT}?sports=${encodeURIComponent(sport)}`
  );

export const fetchCentersByAcademy = (academySlug: string) =>
  secureApi.get<ApiListResponse<PaginatedResponse<TrialCenter>>>(
    `${TRIAL_CENTERS_ENDPOINT}?pageNum=1&academySlug=${encodeURIComponent(academySlug)}`
  );

export const fetchCoursesByCenter = (centerId: string) =>
  secureApi.get<ApiListResponse<TrialCourse[]>>(
    `${TRIAL_COURSES_ENDPOINT}/${centerId}`
  );

export const fetchAgeCategoriesByCourse = (centerId: string, courseId: string) =>
  secureApi.get<ApiListResponse<string[]>>(
    `${TRIAL_AGE_CATEGORIES_ENDPOINT}/${centerId}/${courseId}`
  );

export const fetchAvailableTrialSlots = (
  centerId: string,
  courseId: string,
  ageCategory: string
) =>
  secureApi.get<ApiListResponse<TrialAvailableSlotBatch[]>>(
    `${TRIAL_AVAILABLE_SLOTS_ENDPOINT}?coachingCenter=${centerId}&course=${courseId}&ageCategory=${encodeURIComponent(ageCategory)}`
  );

export const fetchTrialSlotsForDate = (
  courseId: string,
  centerId: string,
  ageCategory: string,
  date: string
) =>
  secureApi.get<ApiListResponse<TrialSlotsForDateResponse>>(
    `${TRIAL_SLOTS_FOR_DATE_ENDPOINT}/${courseId}/${centerId}/${encodeURIComponent(ageCategory)}/${date}`
  );

export const addTrial = (payload: AddTrialPayload) =>
  secureApi.post<ApiListResponse<unknown>>(ADD_TRIAL_ENDPOINT, payload);
